import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROMOTER_COST_URL } from '@insite-store360/shared';
import { catchError, map, Observable } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface PromoterCostSummary {
  id: number;
  countryId: number;
  month: number;
  year: number;
  totalPromos: number;
  totalSalary: number;
  totalCommission: number;
  totalIncentive: number;
  total: number;
  country?: { id: number; name: string; region?: { id: number; name: string } };
}

export interface PromoterCostDetail {
  id: number;
  promoterCostId: number;
  userId: number;
  userName?: string;
  username?: string;
  storeId: number;
  storeName?: string;
  totalSalary: number;
  totalCommission: number;
  totalIncentive: number;
  total: number;
}

export interface UpdatePromoterCostDetailPayload {
  userId?: number;
  storeId?: number;
  totalSalary?: number;
  totalCommission?: number;
  totalIncentive?: number;
}

@Injectable({ providedIn: 'root' })
export class PromoterCostService {
  private readonly BASE = PROMOTER_COST_URL;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService,
  ) {}

  /** Monthly summary rows */
  getSummary(params?: { regionId?: number; countryId?: number; year?: number }): Observable<PromoterCostSummary[]> {
    let qp = new HttpParams();
    if (params?.regionId)  qp = qp.set('regionId', params.regionId);
    if (params?.countryId) qp = qp.set('countryId', params.countryId);
    if (params?.year)      qp = qp.set('year', params.year);
    return this.http.get<any>(`${this.BASE}/summary`, { params: qp })
      .pipe(map(res => res?.result || res), catchError(this.errorHandler.handleError));
  }

  /** Per-user detail rows for a summary row */
  getDetail(promoterCostId: number): Observable<PromoterCostDetail[]> {
    return this.http.get<any>(`${this.BASE}/detail/${promoterCostId}`)
      .pipe(map(res => res?.result || res), catchError(this.errorHandler.handleError));
  }

  /** Upload parsed JSON rows */
  uploadExcel(rows: any[], month: number, year: number, countryId: number): Observable<any> {
    const params = new HttpParams()
      .set('month', month)
      .set('year', year)
      .set('countryId', countryId);
    return this.http.post<any>(`${this.BASE}/upload`, { rows }, { params })
      .pipe(map(res => res?.result || res), catchError(this.errorHandler.handleError));
  }

  /** Download blank Excel template */
  downloadTemplate(countryId?: number): Observable<Blob> {
    let params = new HttpParams();
    if (countryId) params = params.set('countryId', countryId);
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http.get(`${this.BASE}/template`, { headers, responseType: 'blob', params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Export summary + promoter detail as Excel */
  exportExcel(params?: { regionId?: number; countryId?: number; year?: number; promoterCostId?: number }): Observable<Blob> {
    let qp = new HttpParams();
    if (params?.regionId)  qp = qp.set('regionId', params.regionId);
    if (params?.countryId) qp = qp.set('countryId', params.countryId);
    if (params?.year)      qp = qp.set('year', params.year);
    if (params?.promoterCostId) qp = qp.set('promoterCostId', params.promoterCostId);
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http.get(`${this.BASE}/export`, { headers, responseType: 'blob', params: qp })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Edit a user cost detail row */
  updateDetail(id: number, payload: UpdatePromoterCostDetailPayload): Observable<PromoterCostDetail> {
    return this.http.put<any>(`${this.BASE}/detail/${id}`, payload)
      .pipe(map(res => res?.result || res), catchError(this.errorHandler.handleError));
  }

  /** Soft-delete a user cost detail row */
  deleteDetail(id: number): Observable<void> {
    return this.http.delete<any>(`${this.BASE}/detail/${id}`)
      .pipe(map(res => res?.result || res), catchError(this.errorHandler.handleError));
  }
}
